import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { searchDesignsAcrossAccount } from '../services/designSearch';
import { haptics } from '../utils/haptics';

const ROOT_FOLDER = { id: 'root', name: 'My Designs' };
const PAGE_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The Dashboard's design browser (folder stack, continuation-token pagination
 * and the account-wide search) as a hook, for the Service Playlist picker.
 */
export function useDesignBrowser({ enabled, onSearchError }) {
  const { state, isApiReady, fetchDesigns } = useApp();
  const { designs, isDesignsLoading } = state;

  const [folderStack, setFolderStack] = useState([ROOT_FOLDER]);
  const [tokenStack, setTokenStack] = useState(['']);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const nextTokenRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchFolderCacheRef = useRef(new Map());
  const searchDesignPageCacheRef = useRef(new Map());
  const onSearchErrorRef = useRef(onSearchError);
  onSearchErrorRef.current = onSearchError;

  const currentFolderId = folderStack[folderStack.length - 1].id;
  const isSearchActive = debouncedSearchQuery.length > 0;

  const loadPage = useCallback(async ({ folderId, continuation }) => {
    const result = await fetchDesigns({ folderId, continuation: continuation || '', limit: PAGE_LIMIT });
    setHasMore(!!result?.hasMore);
    nextTokenRef.current = result?.continuation || null;
    return result;
  }, [fetchDesigns]);

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (enabled && isApiReady && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadPage({ folderId: 'root', continuation: '' });
    }
  }, [enabled, isApiReady, loadPage]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    if (!debouncedSearchQuery || !isApiReady || !enabled) {
      setSearchResults([]);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);
    setSearchResults([]);

    searchDesignsAcrossAccount({
      query: debouncedSearchQuery,
      limit: PAGE_LIMIT,
      folderCache: searchFolderCacheRef.current,
      designPageCache: searchDesignPageCacheRef.current,
      onProgress: (matches) => {
        if (!cancelled) setSearchResults(matches);
      },
      shouldStop: () => cancelled,
    })
      .catch((err) => {
        if (cancelled) return;
        console.error('Design search failed:', err);
        onSearchErrorRef.current?.();
        setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => { cancelled = true; };
  }, [debouncedSearchQuery, isApiReady, enabled]);

  const openFolder = useCallback(async (stack) => {
    haptics.light();
    setSearchQuery('');
    setFolderStack(stack);
    setTokenStack(['']);
    setPageIndex(0);
    await loadPage({ folderId: stack[stack.length - 1].id, continuation: '' });
  }, [loadPage]);

  const enterFolder = useCallback(
    (folder) => openFolder([...folderStack, { id: folder.folder_id, name: folder.name }]),
    [folderStack, openFolder],
  );

  const navigateToBreadcrumb = useCallback((idx) => {
    if (idx >= folderStack.length - 1) return undefined;
    return openFolder(folderStack.slice(0, idx + 1));
  }, [folderStack, openFolder]);

  const goNext = useCallback(async () => {
    const nextToken = nextTokenRef.current;
    if (!hasMore || isDesignsLoading || !nextToken) return;
    haptics.light();
    setTokenStack((prev) => [...prev, nextToken]);
    setPageIndex((prev) => prev + 1);
    await loadPage({ folderId: currentFolderId, continuation: nextToken });
  }, [hasMore, isDesignsLoading, currentFolderId, loadPage]);

  const goPrev = useCallback(async () => {
    if (pageIndex === 0 || isDesignsLoading) return;
    haptics.light();
    const newTokenStack = tokenStack.slice(0, -1);
    const newPageIndex = pageIndex - 1;
    setTokenStack(newTokenStack);
    setPageIndex(newPageIndex);
    await loadPage({ folderId: currentFolderId, continuation: newTokenStack[newPageIndex] || '' });
  }, [pageIndex, isDesignsLoading, tokenStack, currentFolderId, loadPage]);

  const goFirst = useCallback(async () => {
    if (pageIndex === 0 || isDesignsLoading) return;
    haptics.light();
    setTokenStack(['']);
    setPageIndex(0);
    await loadPage({ folderId: currentFolderId, continuation: '' });
  }, [pageIndex, isDesignsLoading, currentFolderId, loadPage]);

  return {
    folderStack,
    currentFolderId,
    pageIndex,
    hasMore,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    isSearchActive,
    isSearching,
    designs: isSearchActive ? searchResults : designs,
    isLoading: isSearchActive ? isSearching : isDesignsLoading,
    isDesignsLoading,
    enterFolder,
    navigateToBreadcrumb,
    goNext,
    goPrev,
    goFirst,
  };
}
